import React, { useMemo, useState } from "react";
import { calcSIP, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, DarkCTA, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

const sipGoals = ["Retirement", "Child Education", "Wealth Creation", "Custom"];

export const SIPTab = () => {
  const [monthly, setMonthly] = useState(5000);
  const [returnPct, setReturnPct] = useState(12);
  const [years, setYears] = useState(10);
  const [goal, setGoal] = useState("Wealth Creation");

  const result = useMemo(() => calcSIP(monthly, returnPct, years), [monthly, returnPct, years]);
  const animKey = `sip-${Math.round(result.maturity)}-${monthly}-${returnPct}-${years}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Monthly Investment"
          valueText={`Rs. ${formatINR(monthly)}`}
          valueTestId="sip-monthly-value"
          finePrint={<><span>Rs.500</span><span>Rs.1,00,000</span></>}
        >
          <Slider value={monthly} min={500} max={100000} step={500} onChange={setMonthly} ariaLabel="Monthly SIP" />
        </InputRow>

        <InputRow
          label="Expected Annual Return"
          valueText={`${returnPct.toFixed(1)}% per annum`}
          valueTestId="sip-return-value"
          finePrint={<><span>1%</span><span>30% per annum</span></>}
        >
          <Slider value={returnPct} min={1} max={30} step={0.1} onChange={setReturnPct} ariaLabel="Expected return" />
        </InputRow>

        <InputRow
          label="Investment Period"
          valueText={`${years} ${years === 1 ? "Year" : "Years"} (${years * 12} Months)`}
          valueTestId="sip-tenure-value"
          finePrint={<><span>1 year</span><span>40 years</span></>}
        >
          <Slider value={years} min={1} max={40} step={1} onChange={setYears} ariaLabel="Investment period" />
        </InputRow>

        <div>
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>Investment Goal</div>
          <Pills items={sipGoals} value={goal} onChange={setGoal} prefix="sip-goal" />
          <p className="mt-3" style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }}>
            Assumes investments at the start of each month, compounded monthly. Returns are assumed, not
            guaranteed. Mutual fund investments are subject to market risks.
          </p>
        </div>
      </div>

      <RightPanel testId="sip-results">
        <HeroValue label="Maturity Value" value={`Rs. ${formatINR(result.maturity)}`} animKey={animKey} testId="sip-maturity-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Total Invested" value={`Rs. ${formatINR(result.invested)}`} testId="sip-row-invested" />
          <ResultRow label="Est. Returns" value={`Rs. ${formatINR(result.gains)}`} valueColor="#1D9E75" testId="sip-row-gains" />
          <ResultRow label="Total Value" value={`Rs. ${formatINR(result.maturity)}`} testId="sip-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.investedPct}
          rightPct={result.gainsPct}
          leftLabel="Invested"
          rightLabel="Returns"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="sip-split-bar"
        />
        <DarkCTA href="mailto:invest@metamonk.co" testId="sip-start-cta">
          Start a SIP with Meta Monk
        </DarkCTA>
      </RightPanel>
    </div>
  );
};
